import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import {useTranslation} from "react-i18next";
import WinnerService from "../../../api/winnerService";
import {CircularProgress} from "@mui/material";
import Box from "@mui/material/Box";
import {Check, ErrorOutline, Replay} from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import QrReader from "react-qr-reader";
import {getSafeBackendErrorMessage} from "../../../util/utils";

interface IProps {
    open: boolean
    handleDialogClose: () => void
}

const ScanWinnerDialog: FC<IProps> = (props) => {
    const {t} = useTranslation()

    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState(false)
    const [message, setMessage] = useState('')

    useEffect(() => {
        if (props.open) {
            setLoading(false)
            setError(false)
            setSuccess(false)
            setMessage('')
        }
    }, [props.open]);

    const handleScan = useCallback((result: string | null) => {
        if (result) {
            setLoading(true)
            WinnerService.redeemWish(result).then((res) => {
                setSuccess(true)
                setMessage(res.data?.detail ?? '')
            }).catch((e) => {
                setError(true)
                setMessage(getSafeBackendErrorMessage(e.response?.data?.detail))
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [])

    const handleScanAgain = () => {
        setError(false)
    }

    const dialogContent = useMemo(() => {
        if (!loading && !success && !error) {
            return <QrReader facingMode="environment"
                             onScan={handleScan}
                             onError={e => console.error(e)}
            />
        }
        if (loading) {
            return <Box textAlign="center">
                <CircularProgress size={75} color="secondary"/>
            </Box>
        }
        if (error) {
            return <Box textAlign="center">
                <ErrorOutline color="secondary" sx={{fontSize: "6rem"}}/>
                <Typography variant="h5" color="secondary"
                            fontWeight="bold">{message}</Typography>
                <Button startIcon={<Replay/>} color="secondary" variant="outlined" sx={{mt: 2}}
                        onClick={handleScanAgain}>{t("scan_again")}</Button>
            </Box>
        }
        if (success) {
            return <Box textAlign="center">
                <Check color="secondary" sx={{fontSize: "6rem"}}/>
                <Typography variant="h5" color="secondary"
                            fontWeight="bold">{message}</Typography>
            </Box>
        }
        return <></>
    }, [loading, success, error, message, t, handleScan])

    return (
        <CustomDialog open={props.open}
                      title={t('scan_winner')}
                      maxWidth='xs'
                      handleDialogClose={props.handleDialogClose}
                      content={dialogContent}/>
    );
};

export default ScanWinnerDialog;
